import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import theme from '../../../UI/theme';
import { CardHeader, ChartGrid, MetricsContainer } from '../insightsStyles';
import KeyMetric from '../../UiComponents/KeyMetric';
import { MusicIcon } from '../../UiComponents/Icons';
import { FaRegClock } from 'react-icons/fa';
import { DateTime } from "luxon";
import { resolveTimeText } from "../decibel/decibelInsightsUtils";
import WaUsers from '../../UiComponents/Icons/WaUsers';
import { Svg } from '@react-pdf/renderer';


const Grid = styled(ChartGrid)`
    grid-auto-rows: minmax(100px, auto);

    @media only screen and (max-width: ${theme.screenSizes.medium - 1}px) {
        grid-auto-rows: minmax(70px, auto);
    }
`;

const Container = styled.div`
    grid-area: 1 / 1 / 2 / 4;
    background-color: ${theme.colors.darkSpace90};
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;

    @media only screen and (max-width: ${theme.screenSizes.medium - 1}px) {
        padding: 10px;
        grid-gap: 10px;
    }
`;

const KpiMetricContainer = styled(MetricsContainer)`
    grid-template-columns: repeat(2, 1fr);
`



const SvgWrapper = styled.div`
    > svg {
        stroke: ${theme.colors.greenEnergy};
    }
`;

const KpiAutomationInsights = ({dateTime, trendPeriod, regulationsTrends, noiseTrends, getRelevantHourData}) => {


    const [numberOfRegulations, setNumberOfRegulations] = useState(0);
    const [regulationsPrMinute, setRegulationsPrMinute] = useState(0);
    const [peakHour, setPeakHour] = useState('-');
    const [occupancyEstimate, setOccupancyEstimate] = useState('-');

    useEffect(() => {
        calculateMetrics();
    }, [dateTime, trendPeriod, regulationsTrends, noiseTrends]);

    const fmtAmPm = (h) => {
        const hour = ((h % 24) + 24) % 24; // guard negatives
        const suffix = hour >= 12 ? 'PM' : 'AM';
        const h12 = hour % 12 || 12;
        return `${h12} ${suffix}`;
    };


    const calculateMetrics = () => {

        let numRegs = 0;
        let regsPrMin = 0;
        let peakH = -1;
        let occEst = '-';

        // calculate number of regulations
        for (let dayData of regulationsTrends) {
            let hourData = getRelevantHourData(dayData, dateTime?.weekday, trendPeriod) ?? [];
            numRegs += hourData?.reduce((sum, subArray) => sum + subArray?.[1], 0);
        }
        if (trendPeriod === 'DAY') {
            regsPrMin = (numRegs / 24 / 60).toFixed(2);
        } else if (trendPeriod === 'WEEK') {
            regsPrMin = (numRegs / 7 / 24 / 60).toFixed(2);
        } 
        setNumberOfRegulations(numRegs);
        setRegulationsPrMinute(regsPrMin);

        
        // Build one summed series across all noiseTrends, plus per-index contributor counts
        let summedNoiseValues = [];
        let counts = [];

        for (const dayData of (noiseTrends ?? [])) {
        const hourData = getRelevantHourData(dayData, dateTime?.weekday, trendPeriod) ?? [];
        for (let i = 0; i < hourData.length; i++) {
            const raw = hourData[i];
            if (raw != null && Number.isFinite(Number(raw))) {
            const v = Number(raw);
            summedNoiseValues[i] = (summedNoiseValues[i] ?? 0) + v;
            counts[i] = (counts[i] ?? 0) + 1;
            }
        }
        }

        // Per-slot average using only contributing zones (no implicit zeros)
        const averagedNoiseValues = summedNoiseValues.map((sum, i) => (counts[i] ?? 0) > 0 ? sum / counts[i] : 0);


        // Compute peak hour label
        if (summedNoiseValues.length > 0) {
            // Find index of max value
            let peakIdx = -1;
            let peakVal = -Infinity;
            for (let i = 0; i < summedNoiseValues.length; i++) {
                const v = Number.isFinite(Number(summedNoiseValues[i])) ? Number(summedNoiseValues[i]) : -Infinity;
                if (v > peakVal) { peakVal = v; peakIdx = i; }
            }

            if (peakIdx >= 0) {
            if (trendPeriod === 'WEEK') {
                // Flattened week (7*24): derive ISO weekday (1..7) and hour (0..23)
                const peakWeekDay = Math.floor(peakIdx / 24) + 1;
                const hour = peakIdx % 24;

                const dayLabel = DateTime
                .fromJSDate(dateTime?.toJSDate() ?? new Date())
                .set({ weekday: peakWeekDay })
                .toFormat('EEE');

                peakH = `${dayLabel} ${resolveTimeText(hour)}`;  // e.g., "Mon 14:00"
            } else {
                // DAY: index is 0..23 directly
                const hour = peakIdx; 
                peakH = resolveTimeText(hour);                   // e.g., "14:00"
            }
            }
            setPeakHour(peakH);
        }

        // --- occupancy from summed values (>40 mapped to 1..100, >=75 -> 100) ---
        let minLoudnessForOccupancy = 42; //  below this is considered "not open for business"
        let fullOccupancyLoudness = 75; // at or above this is considered "full occupancy"
        const occupancyDataPoints = averagedNoiseValues.filter(v => Number(v) > minLoudnessForOccupancy);
        const mappedOccupancyDataPoints = occupancyDataPoints.map(vx => {
            let v = Number(vx);
            if (v <= minLoudnessForOccupancy) return 1;
            else if (v < 50) {
                // map minLoudnessForOccupancy..50 to 1..50
                return Math.round(1 + ((v - minLoudnessForOccupancy) / (50 - minLoudnessForOccupancy)) * 49);
            } else {
                // return linear following 0 if v=50 is 50, v=fullOccupancyLoudness is 100
                return Math.round(50 + ((v - 50) / (fullOccupancyLoudness - 50)) * 50);
            }
            
        });
        if (mappedOccupancyDataPoints.length > 0) {
            occEst = Math.round(
                mappedOccupancyDataPoints.reduce((s, v) => s + v, 0) / mappedOccupancyDataPoints.length
            );
        }
        setOccupancyEstimate(occEst);

    }

    

    return (
        <Grid>
            <Container>
                <CardHeader>Venue Statistics</CardHeader>
                <KpiMetricContainer>
                    <KeyMetric
                        metric={numberOfRegulations}
                        title={`Adjustments`}
                        subTitle={`that's ${Math.round(regulationsPrMinute * 60)} every hour!`}
                        icon={<MusicIcon />}
                    />
                    <KeyMetric
                        metric={peakHour}
                        title={`Peak Hour`}
                        subTitle={`averaged at your venue`}
                        icon={<FaRegClock />}
                    />
                    {/*
                    <KeyMetric
                        metric={`${occEst}%`}
                        title={`Occupancy`}
                        subTitle={`- in active hours`}
                        icon={<SvgWrapper><WaUsers /></SvgWrapper>}
                    />
                    */}
                </KpiMetricContainer>
            </Container>
        </Grid>
        
    );
}

export default KpiAutomationInsights;